import { Command, Validation } from "../../lib";

export type LobbyGameSettings = {
  minPlayerCount: number;
  maxPlayerCount: number;
  params?: Record<string, string>;
};

export type LobbyUser = {
  id: string;
  name: string;
};

export type LobbyGame = {
  id: string;
  users: LobbyUser[];
  settings: LobbyGameSettings;
  started: boolean;
  endedBy?: string;
};

export type NewLobbyGame = {
  id: string;
  settings: LobbyGameSettings;
};

export const LobbyGame = {
  isInGame(game: LobbyGame, userId: string) {
    return game.users.map(({ id }) => id).includes(userId);
  },
  isAdmin(game: LobbyGame, userId: string) {
    return game.users.at(0)?.id === userId;
  },
  isLaunchable(game: LobbyGame) {
    const {
      users,
      settings: { maxPlayerCount, minPlayerCount },
    } = game;
    return minPlayerCount <= users.length && users.length <= maxPlayerCount;
  },
  isOpenable(game: LobbyGame, userId: string): boolean {
    return this.isInGame(game, userId) && game.started;
  },
};

export type LobbyState = {
  games: LobbyGame[];
};

export const LobbyState = {
  getGame(state: LobbyState, gameId: string) {
    return state.games.find((game) => game.id === gameId);
  },
};

export const lobbyValidations = (state: LobbyState, sender: LobbyUser) => {
  return {
    create(props: NewLobbyGame): Validation {
      const game = LobbyState.getGame(state, props.id);
      if (game) return "a game with this id already exist";
      return;
    },
    join(gameId: string): Validation {
      const game = LobbyState.getGame(state, gameId);
      if (!game) return "no game";
      const isInGame = LobbyGame.isInGame(game, sender.id);
      if (isInGame) return "already in game";
      if (game.started) return "game already started";
      if (game.users.length >= game.settings.maxPlayerCount) {
        return "not enough space for a new player";
      }
      return;
    },
    leave(gameId: string): Validation {
      const game = LobbyState.getGame(state, gameId);
      if (!game) return "no game";
      const isInGame = LobbyGame.isInGame(game, sender.id);
      if (!isInGame) return "not in game";
      return;
    },
    updateSettings({
      gameId,
    }: {
      gameId: string;
      settings: LobbyGameSettings;
    }): Validation {
      const game = LobbyState.getGame(state, gameId);
      if (!game) return "no game";
      if (!LobbyGame.isAdmin(game, sender.id)) return "only allowed to admin";
      return;
    },
    start(gameId: string): Validation {
      const game = LobbyState.getGame(state, gameId);
      if (!game) return "no game";
      if (!LobbyGame.isAdmin(game, sender.id)) return "only allowed to admin";
      if (!LobbyGame.isLaunchable(game)) return "number of players not right";
      if (game.started) return "game already started";
    },
  };
};

export const LobbyStateUpdate = (lobbyState: LobbyState, sender: LobbyUser) => {
  const getGame = (gameId: string) => LobbyState.getGame(lobbyState, gameId);

  const deleteGame = (gameId: string) => {
    lobbyState.games = lobbyState.games.filter((game) => game.id !== gameId);
  };
  const validations = lobbyValidations(lobbyState, sender);

  return {
    join(gameId: string) {
      const game = getGame(gameId);
      if (!game || validations.join(gameId)) {
        return;
      }
      game.users.push(sender);
    },
    leave(gameId: string) {
      const game = getGame(gameId);
      if (!game || validations.leave(gameId)) return;
      if (game.started) {
        game.endedBy = `${sender.name} left the game`;
      } else {
        game.users = game.users.filter((user) => user.id !== sender.id);
        if (!game.users.length) {
          deleteGame(gameId);
        }
      }
    },
    create(props: NewLobbyGame) {
      if (validations.create(props)) return;
      const game: LobbyGame = {
        ...props,
        users: [sender],
        started: false,
      };
      lobbyState.games.push(game);
    },
    updateSettings({
      gameId,
      settings,
    }: {
      gameId: string;
      settings: LobbyGameSettings;
    }) {
      const game = getGame(gameId);
      if (!game || validations.updateSettings({ gameId, settings })) return;
      game.settings = settings;
    },
    start(gameId: string) {
      const game = getGame(gameId);
      if (!game || validations.start(gameId)) {
        return;
      }
      game.started = true;
    },
    updateUserProps({
      userId,
      props,
    }: {
      userId: string;
      props: Partial<Omit<LobbyUser, "id">>;
    }) {
      lobbyState.games.forEach((game) => {
        game.users.forEach((user) => {
          if (user.id === userId) {
            Object.assign(user, props);
          }
        });
      });
    },
  };
};

type LobbyApi = ReturnType<typeof LobbyStateUpdate>;
export type LobbyCommand = Command<LobbyApi> & {
  sender: LobbyUser;
};

const emptyState: LobbyState = {
  games: [],
};
const emptyUser: LobbyUser = {
  id: "",
  name: "",
};

export const lobbyCommandBuilder = Command.builder(
  LobbyStateUpdate(emptyState, emptyUser)
);

export const lobbyReducer = (
  state: LobbyState,
  command: LobbyCommand
): LobbyState => {
  console.info("apply command", command);
  Command.call(LobbyStateUpdate(state, command.sender), command);
  return state;
};

const LobbyStateUndo = (lobbyState: LobbyState, sender: LobbyUser) => {
  const getGame = (gameId: string) => LobbyState.getGame(lobbyState, gameId);

  return {
    join(gameId: string) {
      const game = getGame(gameId);
      if (!game) {
        return;
      }
      game.users = game.users.filter((user) => user.id !== sender.id);
    },
    leave(gameId: string) {
      const game = getGame(gameId);
      if (!game) return;
      game.users.push(sender);
    },
    start(gameId: string) {
      const game = getGame(gameId);
      if (!game) return;
      game.started = false;
    },
  };
};

export const lobbyUndoReducer = (
  state: LobbyState,
  command: LobbyCommand
): LobbyState => {
  console.info("undo command", command);
  const undo = LobbyStateUndo(state, command.sender);
  const { methodName, arg: rawArg } = command;
  switch (methodName) {
    case "join": {
      const gameId = rawArg as string;
      undo.join(gameId);
      break;
    }
    case "leave": {
      const gameId = rawArg as string;
      undo.leave(gameId);
      break;
    }
    case "start": {
      const gameId = rawArg as string;
      undo.start(gameId);
      break;
    }
    default: {
      break;
    }
  }
  return state;
};
